import { Link, router } from '@inertiajs/react';
import { motion } from 'framer-motion';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';

export default function Index({ query, quotations, statuses, statusColors }) {
    const handleDelete = (quotation) => {
        if (confirm(`Are you sure you want to delete ${quotation.quotation_number}?`)) {
            router.delete(`/queries/${query.id}/quotations/${quotation.id}`);
        }
    };

    const handleDuplicate = (quotation) => {
        router.post(`/queries/${query.id}/quotations/${quotation.id}/duplicate`);
    };

    const handleStatusChange = (quotation, status) => {
        router.patch(`/queries/${query.id}/quotations/${quotation.id}/status`, {
            status,
        });
    };

    const getStatusColor = (status) => {
        return statusColors[status] || 'secondary';
    };

    const formatCurrency = (amount, currency) => {
        if (!amount) return '-';
        const symbol = currency?.symbol || '₹';
        return `${symbol}${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const breadcrumbs = [
        { label: 'Queries', href: '/queries' },
        { label: query.query_number, href: `/queries/${query.id}` },
        { label: 'Quotations' },
    ];

    return (
        <AppLayout title="Quotations">
            <PageHeader
                title="Quotations"
                subtitle={`${query.query_number} - ${query.client?.name || query.guest_name}`}
                breadcrumbs={breadcrumbs}
                actions={
                    <Link
                        href={`/queries/${query.id}/quotations/create`}
                        className="btn btn-primary"
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        New Quotation
                    </Link>
                }
            />

            {quotations.length === 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-file-earmark-text display-4 text-muted mb-3 d-block"></i>
                        <h5 className="text-muted mb-2">No quotations yet</h5>
                        <p className="text-muted mb-3">
                            Create your first quotation to start building the itinerary
                        </p>
                        <Link
                            href={`/queries/${query.id}/quotations/create`}
                            className="btn btn-primary"
                        >
                            <i className="bi bi-plus-lg me-1"></i>
                            Create Quotation
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="row g-4">
                    {quotations.map((quotation, index) => (
                        <motion.div
                            key={quotation.id}
                            className="col-md-6 col-lg-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h6 className="mb-1">
                                                <Link
                                                    href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                                                    className="text-decoration-none text-dark"
                                                >
                                                    {quotation.quotation_number}
                                                </Link>
                                            </h6>
                                            <p className="text-muted small mb-0">
                                                {quotation.title || 'Untitled'}
                                            </p>
                                        </div>
                                        <div className="dropdown">
                                            <button
                                                className={`btn btn-sm btn-${getStatusColor(quotation.status)}-subtle dropdown-toggle`}
                                                type="button"
                                                data-bs-toggle="dropdown"
                                            >
                                                {statuses[quotation.status]}
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end">
                                                {Object.entries(statuses).map(([key, label]) => (
                                                    <li key={key}>
                                                        <button
                                                            className={`dropdown-item ${quotation.status === key ? 'active' : ''}`}
                                                            onClick={() => handleStatusChange(quotation, key)}
                                                        >
                                                            {label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-3">
                                        <div className="col-6">
                                            <div className="small text-muted">Travel</div>
                                            <div className="fw-medium">
                                                {quotation.travel_date
                                                    ? new Date(quotation.travel_date).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                    })
                                                    : '-'}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="small text-muted">Duration</div>
                                            <div className="fw-medium">
                                                {quotation.nights}N/{(quotation.nights || 0) + 1}D
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="small text-muted">Travelers</div>
                                            <div className="fw-medium">
                                                {quotation.adults + (quotation.children || 0) + (quotation.infants || 0)} pax
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="small text-muted">Services</div>
                                            <div className="fw-medium">{quotation.items_count || 0}</div>
                                        </div>
                                    </div>

                                    <div className="bg-light rounded p-3 mb-3">
                                        <div className="d-flex justify-content-between">
                                            <span className="small text-muted">Selling Price</span>
                                            <span className="fw-bold text-primary">
                                                {formatCurrency(quotation.total_selling, quotation.currency)}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="small text-muted">Cost</span>
                                            <span className="small">
                                                {formatCurrency(quotation.total_cost, quotation.currency)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <Link
                                            href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                                            className="btn btn-sm btn-outline-primary flex-fill"
                                        >
                                            <i className="bi bi-pencil me-1"></i>
                                            Edit
                                        </Link>
                                        <div className="dropdown">
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                            >
                                                <i className="bi bi-three-dots-vertical"></i>
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end">
                                                <li>
                                                    <Link
                                                        href={`/queries/${query.id}/quotations/${quotation.id}`}
                                                        className="dropdown-item"
                                                    >
                                                        <i className="bi bi-eye me-2"></i>
                                                        Preview
                                                    </Link>
                                                </li>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleDuplicate(quotation)}
                                                    >
                                                        <i className="bi bi-copy me-2"></i>
                                                        Duplicate
                                                    </button>
                                                </li>
                                                <li><hr className="dropdown-divider" /></li>
                                                <li>
                                                    <button
                                                        className="dropdown-item text-danger"
                                                        onClick={() => handleDelete(quotation)}
                                                    >
                                                        <i className="bi bi-trash me-2"></i>
                                                        Delete
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer bg-transparent border-0 small text-muted">
                                    Version {quotation.version}
                                    {quotation.is_final && (
                                        <span className="badge bg-success-subtle text-success ms-2">Final</span>
                                    )}
                                    <span className="float-end">
                                        {quotation.created_by?.name}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </AppLayout>
    );
}
